import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Pencil, Plus, Ticket, Trash2 } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import { couponsResponse } from '../../lib/types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { formatDateTime, formatMoney } from '../../lib/format'
import { DateTimePickerField } from '../../components/ui/DateTimePickerField'
import {
  parseDateTimeInput,
  formatDateTimeInput,
  dateTimePlaceholder,
} from '../../components/ui/dateTimeInput'

/** Derived Status shown in the table — mirrors the backend's
 *  `couponApplicabilityError` order-of-checks so the pill matches what
 *  the redemption endpoint would say if a buyer tried this code right
 *  now. Precedence: admin toggle off → scheduled → expired → exhausted
 *  → active (bug 0016559: without the derivation an expired coupon
 *  would still render green "Active"). */
/** Mirrors `maxRedemptions` in the API's couponBaseDto — the two must
 *  move together, since the server is what actually blocks the save. */
const MAX_REDEMPTIONS = 100_000

function deriveCouponStatus(c, now) {
  if (!c.isActive) return 'off'
  if (c.validFrom && now < new Date(c.validFrom)) return 'scheduled'
  if (c.validUntil && now > new Date(c.validUntil)) return 'expired'
  if (
    c.maxRedemptions != null &&
    Number(c.redemptionCount ?? 0) >= Number(c.maxRedemptions)
  ) {
    return 'exhausted'
  }
  return 'active'
}

const STATUS_STYLE = {
  active:
    'bg-[color:var(--color-success)] text-white dark:bg-white/[0.06] dark:text-emerald-300',
  expired:
    'bg-[color:var(--color-priority-urgent)] text-white dark:bg-white/[0.06] dark:text-rose-300',
  exhausted:
    'bg-[color:var(--color-priority-urgent)] text-white dark:bg-white/[0.06] dark:text-rose-300',
  scheduled:
    'bg-[color:var(--color-accent)] text-white dark:bg-white/[0.06] dark:text-sky-300',
  off:
    'bg-[color:var(--color-ink-muted)] text-white dark:bg-white/[0.06] dark:text-slate-300',
}

const STATUS_LABEL = {
  active: 'Active',
  expired: 'Expired',
  exhausted: 'Exhausted',
  scheduled: 'Scheduled',
  off: 'Off',
}

export function CouponsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [bulkDel, setBulkDel] = useState(null) // { ids, clearSelection } | null
  const [bulkRunning, setBulkRunning] = useState(false)

  const couponsQuery = useQuery({
    queryKey: ['platform-coupons'],
    queryFn: async () => {
      const res = await api.get('/super-admin/coupons')
      return couponsResponse.parse(res.data)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/super-admin/coupons/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Coupon deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['platform-coupons'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rows = couponsQuery.data?.data ?? []

  // Bulk Delete — fan out to the SAME per-coupon endpoint the row menu uses.
  const bulkActions = [
    {
      label: 'Delete',
      icon: Trash2,
      danger: true,
      onClick: (ids, ctx) =>
        setBulkDel({ ids, clearSelection: ctx?.clearSelection }),
    },
  ]
  async function runBulkDelete() {
    if (!bulkDel) return
    setBulkRunning(true)
    let ok = 0
    const fails = []
    for (const id of bulkDel.ids) {
      try {
        await api.delete(`/super-admin/coupons/${id}`)
        ok += 1
      } catch (e) {
        fails.push(extractApiError(e).message)
      }
    }
    setBulkRunning(false)
    if (ok) toast.success(`${ok} ${ok === 1 ? 'coupon' : 'coupons'} deleted.`)
    if (fails.length)
      toast.error(
        `${fails.length} failed: ${fails[0]}` +
          (fails.length > 1 ? ` (+${fails.length - 1} more)` : ''),
      )
    await qc.invalidateQueries({ queryKey: ['platform-coupons'] })
    bulkDel.clearSelection?.()
    setBulkDel(null)
  }

  // Status is DERIVED per row against the current clock (admin toggle →
  // scheduled → expired → exhausted → active). Each accessor reads a fresh
  // `new Date()` so the pill/sort/filter never go stale within a session.
  const columns = useMemo(
    () => [
      {
        key: 'code',
        header: 'Code',
        pinnable: true,
        minWidth: 140,
        sortAccessor: (c) => (c.code ?? '').toLowerCase(),
        searchAccessor: (c) => c.code ?? '',
        exportValue: (c) => c.code ?? '',
        render: (c) => (
          <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
            {c.code}
          </span>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        grow: true,
        minWidth: 220,
        sortAccessor: (c) => (c.description ?? '').toLowerCase(),
        searchAccessor: (c) => c.description ?? '',
        exportValue: (c) => c.description ?? '',
        render: (c) => (
          <span className="block truncate text-[color:var(--color-ink-muted)]">
            {c.description ?? '—'}
          </span>
        ),
      },
      {
        key: 'discount',
        header: 'Discount',
        minWidth: 110,
        sortAccessor: (c) => Number(c.discountValue ?? 0),
        searchAccessor: (c) => String(c.discountValue ?? ''),
        exportValue: (c) =>
          c.discountType === 'percentage'
            ? `${Number(c.discountValue)}%`
            : formatMoney(c.discountValue, c.currency),
        cellClassName:
          'font-medium text-[color:var(--color-ink-strong)] tabular-nums',
        render: (c) =>
          c.discountType === 'percentage'
            ? `${Number(c.discountValue)}%`
            : formatMoney(c.discountValue, c.currency),
      },
      {
        key: 'redemptions',
        header: 'Redemptions',
        minWidth: 110,
        sortAccessor: (c) => Number(c.redemptionCount ?? c.timesRedeemed ?? 0),
        exportValue: (c) =>
          `${c.redemptionCount}${c.maxRedemptions ? ` / ${c.maxRedemptions}` : ' / ∞'}`,
        cellClassName:
          'text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap',
        render: (c) => (
          <>
            {c.redemptionCount}
            {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ' / ∞'}
          </>
        ),
      },
      {
        key: 'validUntil',
        header: 'Valid Until',
        minWidth: 150,
        sortAccessor: (c) => (c.validUntil ? new Date(c.validUntil) : null),
        exportValue: (c) => (c.validUntil ? formatDateTime(c.validUntil) : ''),
        cellClassName:
          'text-[color:var(--color-ink-muted)] text-xs whitespace-nowrap',
        render: (c) => (c.validUntil ? formatDateTime(c.validUntil) : '—'),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 120,
        sortAccessor: (c) => deriveCouponStatus(c, new Date()),
        exportValue: (c) => STATUS_LABEL[deriveCouponStatus(c, new Date())],
        render: (c) => {
          const s = deriveCouponStatus(c, new Date())
          return (
            <span
              className={
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border border-transparent dark:border-white/10 ' +
                STATUS_STYLE[s]
              }
              title={
                s === 'expired'
                  ? `Valid Until ${formatDateTime(c.validUntil)} has passed`
                  : s === 'exhausted'
                    ? `Redemption cap of ${c.maxRedemptions} reached`
                    : s === 'scheduled'
                      ? `Valid From ${formatDateTime(c.validFrom)}`
                      : s === 'off'
                        ? 'Turned off by an admin'
                        : undefined
              }
            >
              {STATUS_LABEL[s]}
            </span>
          )
        },
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (c) => (
          <RowActionsMenu
            label={`Actions for ${c.code}`}
            items={[
              {
                key: 'edit',
                label: 'Edit',
                icon: Pencil,
                onClick: () => setEditing(c),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                onClick: () => setConfirmDelete(c),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  const filterConfig = useMemo(
    () => [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        anyLabel: 'All statuses',
        filterAccessor: (c) => deriveCouponStatus(c, new Date()),
        options: [
          { value: 'active', label: 'Active' },
          { value: 'scheduled', label: 'Scheduled' },
          { value: 'expired', label: 'Expired' },
          { value: 'exhausted', label: 'Exhausted' },
          { value: 'off', label: 'Off' },
        ],
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Coupons" homePath="/super-admin/dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Ticket size={20} className="text-[color:var(--color-accent-strong)]" />
            Coupons
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Create discount codes for sign-up and checkout.
          </p>
        </div>
        <Button className="shrink-0 self-start" onClick={() => setEditing('new')}>
          <Plus size={14} />
          New Coupon
        </Button>
      </div>

      <DataTable
        tableId="platform-coupons"
        columns={columns}
        rows={rows}
        getRowId={(c) => c.id}
        loading={couponsQuery.isLoading}
        fetching={couponsQuery.isFetching}
        error={
          couponsQuery.isError
            ? extractApiError(couponsQuery.error).message
            : null
        }
        onRetry={() => couponsQuery.refetch()}
        title="Coupons"
        exportBaseName="coupons"
        exportTitle="Coupons"
        selectable
        bulkActions={bulkActions}
        searchPlaceholder="Search Code, Description or Discount"
        filterConfig={filterConfig}
        defaultSort={[{ key: 'code', dir: 'asc' }]}
        emptyState={{
          icon: Ticket,
          title: 'No coupons yet',
          body: 'Add your first promo code.',
        }}
      />

      {editing ? (
        <CouponDialog
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Coupon"
        message={
          confirmDelete
            ? `Delete coupon "${confirmDelete.code}"? Existing redemptions are preserved; the code just stops accepting new ones.`
            : ''
        }
        confirmLabel="Delete Coupon"
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) removeMutation.mutate(confirmDelete.id)
        }}
      />

      <ConfirmDialog
        open={bulkDel !== null}
        title={`Delete ${bulkDel?.ids.length ?? 0} ${(bulkDel?.ids.length ?? 0) === 1 ? 'coupon' : 'coupons'}`}
        message={`Delete ${bulkDel?.ids.length ?? 0} selected ${(bulkDel?.ids.length ?? 0) === 1 ? 'coupon' : 'coupons'}? Existing redemptions are preserved; the codes just stop accepting new ones.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={bulkRunning}
        onCancel={() => {
          if (!bulkRunning) setBulkDel(null)
        }}
        onConfirm={runBulkDelete}
      />
    </div>
  )
}

function CouponDialog({
  initial,
  onClose,
}) {
  const qc = useQueryClient()
  const isEdit = Boolean(initial)
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    description: initial?.description ?? '',
    discountType: initial?.discountType ?? 'percentage',
    discountValue:
      initial?.discountValue != null ? String(initial.discountValue) : '',
    currency: initial?.currency ?? 'USD',
    maxRedemptions:
      initial?.maxRedemptions != null ? String(initial.maxRedemptions) : '',
    validFrom: formatDateTimeInput(initial?.validFrom),
    validUntil: formatDateTimeInput(initial?.validUntil),
    isActive: initial?.isActive ?? true,
  })
  const [errors, setErrors] = useState({})

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) {
      const { [key]: _omit, ...rest } = errors
      void _omit
      setErrors(rest)
    }
  }

  /** Validate every field and return a per-key errors object. Empty
   *  object = form is valid. Called on submit so the user always sees
   *  WHY a click was rejected instead of staring at a disabled button. */
  function validate() {
    const next = {}
    if (!form.code.trim()) {
      next.code = 'Code is required.'
    } else if (form.code.trim().length > 50) {
      next.code = 'Code must be at most 50 characters.'
    }
    // The field soft-limits and shows "too long" past 200, but nothing
    // stopped the submit and the server had no cap — so the warning was
    // shown and the value saved anyway (QA, 2026-09-01).
    if ((form.description ?? '').trim().length > 200) {
      next.description = 'Description must be at most 200 characters.'
    }
    const value = Number(form.discountValue)
    if (form.discountValue === '' || form.discountValue === null) {
      next.discountValue = 'Value is required.'
    } else if (!Number.isFinite(value) || value <= 0) {
      next.discountValue = 'Value must be greater than 0.'
    } else if (form.discountType === 'percentage' && value > 100) {
      next.discountValue = 'Percentage discount cannot exceed 100.'
    }
    if (form.discountType === 'amount') {
      const cur = (form.currency ?? '').trim()
      if (cur.length !== 3) {
        next.currency = 'Currency must be a 3-letter code.'
      }
    }
    // Validity window. "Valid From" stays optional (a coupon can start
    // the moment it's created). "Valid Until" is REQUIRED — a coupon
    // with no expiry drifts forever and silently keeps discounting long
    // after the campaign it was cut for is done (bug 0013263). Each
    // entered value must parse in the DD/MM/YYYY HH:MM (AM/PM) format,
    // and "until" must not precede "from".
    const fromStr = form.validFrom.trim()
    const untilStr = form.validUntil.trim()
    const from = fromStr ? parseDateTimeInput(fromStr) : null
    const until = untilStr ? parseDateTimeInput(untilStr) : null
    if (fromStr && !from) {
      next.validFrom = `Valid from must be in ${dateTimePlaceholder()} format.`
    }
    if (!untilStr) {
      next.validUntil = 'Valid until date is required.'
    } else if (!until) {
      next.validUntil = `Valid until must be in ${dateTimePlaceholder()} format.`
    }
    // Strictly later — equal timestamps make a zero-length window (a coupon
    // that expires the instant it starts). Compares Luxon DateTimes, which
    // order by absolute millis, so the check is timezone-safe.
    if (from && until && until <= from) {
      next.validUntil = 'Valid Until must be later than Valid From.'
    }
    // A coupon must not be born (or re-dated) into the past — a past "Valid
    // until" creates a code that is Expired the instant it's saved. Enforce a
    // future expiry on create; on edit, only when the date is actually being
    // CHANGED, so an unrelated tweak (e.g. description) to an already-expired
    // coupon can still be saved without moving its end date.
    if (until && !next.validUntil) {
      const untilChanged = untilStr !== formatDateTimeInput(initial?.validUntil)
      if (untilChanged && until.toMillis() <= Date.now()) {
        next.validUntil = 'Valid until must be a future date and time.'
      }
    }
    if (form.maxRedemptions) {
      const maxR = Number(form.maxRedemptions)
      if (!Number.isFinite(maxR) || maxR < 1 || !Number.isInteger(maxR)) {
        next.maxRedemptions = 'Max uses must be a positive whole number.'
      } else if (maxR > MAX_REDEMPTIONS) {
        // Upper bound, mirroring the API. Without it a value like 1e+99
        // sailed through every check above (it IS finite, >= 1, and
        // Number.isInteger(1e99) is true) and only failed server-side at
        // the safe-integer boundary with a raw Zod error (QA, 2026-09-01).
        next.maxRedemptions = 'Max uses must be at most 100,000.'
      } else if (
        initial?.redemptionCount &&
        maxR < initial.redemptionCount
      ) {
        next.maxRedemptions = `Cannot be lower than current redemptions (${initial.redemptionCount}).`
      }
    }
    return next
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const value = Number(form.discountValue)
      const maxR = form.maxRedemptions ? Number(form.maxRedemptions) : null
      const body = {
        code: form.code.trim(),
        description: form.description.trim() || null,
        discountType: form.discountType,
        discountValue: value,
        currency:
          form.discountType === 'amount'
            ? form.currency.trim().toUpperCase().slice(0, 3)
            : null,
        maxRedemptions: maxR,
        validFrom: form.validFrom.trim()
          ? parseDateTimeInput(form.validFrom).toJSDate().toISOString()
          : null,
        validUntil: form.validUntil.trim()
          ? parseDateTimeInput(form.validUntil).toJSDate().toISOString()
          : null,
        isActive: form.isActive,
      }
      if (initial) {
        const res = await api.patch(`/super-admin/coupons/${initial.id}`, body)
        return res.data
      }
      const res = await api.post('/super-admin/coupons', body)
      return res.data
    },
    onSuccess: () => {
      toast.success(initial ? 'Coupon updated successfully.' : 'Coupon created successfully.')
      void qc.invalidateQueries({ queryKey: ['platform-coupons'] })
      onClose()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) setErrors(fields)
      else toast.error(message)
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Coupon' : 'Add Coupon'}
      description={
        isEdit
          ? `Update the ${initial?.code ?? ''} promo code.`
          : 'A promo code redeemable during Sign-up or plan checkout.'
      }
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            form="coupon-form"
            type="submit"
            loading={saveMutation.isPending}
          >
            {isEdit ? 'Save Changes' : 'Add'}
          </Button>
        </>
      }
    >
      <form
        id="coupon-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (saveMutation.isPending) return
          const next = validate()
          setErrors(next)
          if (Object.keys(next).length === 0) saveMutation.mutate()
        }}
        noValidate
        className="space-y-3"
      >
        <TextField
          label="Code"
          required
          placeholder="Enter the Code"
          maxLength={50}
          value={form.code}
          onChange={(e) => setField('code', e.target.value.toUpperCase())}
          error={errors.code}
          // Force the typed code uppercase, but leave the placeholder in
          // its natural case so it reads "Enter Code", not "ENTER CODE".
          className="uppercase placeholder:normal-case"
        />
        <TextField
          label="Description"
          placeholder="Enter a Description"
          maxLength={200}
          value={form.description ?? ''}
          onChange={(e) => setField('description', e.target.value)}
          error={errors.description}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <FieldLabel label="Type" required htmlFor="coupon-type" />
            <select
              id="coupon-type"
              value={form.discountType}
              onChange={(e) => setField('discountType', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
            >
              <option value="percentage">Percentage off</option>
              <option value="amount">Flat amount</option>
            </select>
          </div>
          <TextField
            label="Value"
            required
            type="number"
            step="0.01"
            min={0}
            max={form.discountType === 'percentage' ? 100 : undefined}
            placeholder="Enter the Value"
            value={form.discountValue}
            onChange={(e) => setField('discountValue', e.target.value)}
            error={errors.discountValue}
          />
          {form.discountType === 'amount' ? (
            <div>
              <FieldLabel label="Currency" required htmlFor="coupon-currency" />
              <select
                id="coupon-currency"
                value={form.currency}
                onChange={(e) => setField('currency', e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
              >
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="INR">INR — Indian Rupee</option>
                <option value="CAD">CAD — Canadian Dollar</option>
                <option value="AUD">AUD — Australian Dollar</option>
              </select>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DateTimePickerField
            label="Valid From"
            id="coupon-valid-from"
            value={form.validFrom}
            onChange={(v) => setField('validFrom', v)}
            error={errors.validFrom}
          />
          <DateTimePickerField
            label="Valid Until"
            id="coupon-valid-until"
            required
            value={form.validUntil}
            onChange={(v) => setField('validUntil', v)}
            error={errors.validUntil}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField
            label="Max Uses"
            type="number"
            min={1}
            placeholder="Unlimited"
            value={form.maxRedemptions}
            onChange={(e) => setField('maxRedemptions', e.target.value)}
            error={errors.maxRedemptions}
          />
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setField('isActive', e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
          />
          <span className="text-sm">Active</span>
        </label>
      </form>
    </Modal>
  )
}
